import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Shield, Phone, Mail } from 'lucide-react';
import { generateWebPageSchema, generateBreadcrumbSchema } from '../utils/schemaMarkup';
import { useSchemaMarkup } from '../utils/useSchemaMarkup';

const PrivacyPolicyPage = () => {
  // Generate schemas
  const webPageSchema = generateWebPageSchema({
    name: 'Privacy Policy - ComfortMaster Heating & Air',
    description: 'Privacy Policy for Comfort Master Heating & Air. Learn how we collect, use, and protect your personal information.',
    url: 'https://comfortmasterheating.com/privacy-policy',
    datePublished: '2024-04-27',
    dateModified: '2024-04-27'
  });
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: 'https://comfortmasterheating.com' },
    { name: 'Privacy Policy', url: 'https://comfortmasterheating.com/privacy-policy' }
  ]);

  // Inject schemas into document head
  useSchemaMarkup([webPageSchema, breadcrumbSchema], 'privacy-policy');

  return (
    <main>
      <Helmet>
        <title>Privacy Policy | ComfortMaster Heating &amp; Air</title>
        <meta name="description" content="Privacy Policy for Comfort Master Heating &amp; Air. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://comfortmasterheating.com/privacy-policy" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-emerald-400" />
            <span className="text-emerald-400 font-semibold text-sm uppercase tracking-wider">Legal</span>
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl mb-4">
            Privacy Policy
          </h1>
          <p className="text-stone-400 mt-2">Effective Date: April 27, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            <p className="text-slate-700 mb-8 text-lg leading-relaxed">
              Comfort Master Heating &amp; Air ("Company," "we," "our," or "us") is committed to protecting your privacy.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">1. Information We Collect</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Name, phone, email, service address, and service request details. Also IP, browser, and usage data.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">2. How We Use Your Information</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Appointments, communication, reminders, support, improvements, and marketing (if opted in).
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">3. SMS/Text Messaging</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              You consent to receive SMS including reminders, updates, and promotions. Message frequency may vary. Message and data rates may apply.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">4. Opt-Out</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Reply STOP to unsubscribe or HELP for assistance.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">5. Data Protection</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We do NOT sell or share personal data for marketing. We do not sell, rent, or share your opt-in data and phone numbers with third parties for marketing or promotional purposes.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">6. Usage Transparency</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Used only for service delivery and communication.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">7. Cookies</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Used for performance and UX.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">8. Security</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We protect your data with safeguards.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">9. Your Rights</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              Access, update, or delete your data.
            </p>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">10. Contact Information</h2>
            <div className="bg-stone-100 rounded-xl p-6 mb-6">
              <p className="font-bold text-slate-900 mb-3">Comfort Master Heating &amp; Air</p>
              <div className="flex items-center gap-3 mb-3">
                <Phone className="w-5 h-5 text-emerald-600" />
                <a href="tel:8593576803" className="text-emerald-600 font-semibold">(859) 357-6803</a>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-emerald-600" />
                <a href="mailto:info@comfortmasterheating.com" className="text-emerald-600 font-semibold">info@comfortmasterheating.com</a>
              </div>
            </div>

            <h2 className="font-heading font-bold text-2xl text-slate-900 mt-8 mb-4">11. Updates</h2>
            <p className="text-slate-700 mb-6 leading-relaxed">
              We may update this policy periodically.
            </p>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="text-slate-600 text-sm">
                See also our <Link to="/terms-conditions" className="text-emerald-600 hover:underline">Terms &amp; Conditions</Link> for information on service usage.
              </p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default PrivacyPolicyPage;
